import { useMemo, useState } from "react";
import {
	useReactTable,
	getCoreRowModel,
	getExpandedRowModel,
	flexRender,
	ExpandedState,
} from "@tanstack/react-table";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { ArrowUp, ArrowDown } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../../components/ui/table";
import {
	RecipientTableRow,
	SammelrechnungTableActions,
	CheckboxState,
} from "../sammelrechnungen-table.types";
import { createSammelrechnungColumns } from "../sammelrechnungen-columns";
import {
	transformToTableData,
	isInDateRange,
	getVisibleColumnsForRecipient,
	getAvailableCheckboxStates,
	getNextCheckboxState,
	getCheckboxState,
	getOptimalColumnWidths,
	hasAnyConflictsInData,
	sortJobDocuments,
} from "../sammelrechnungen-table.utils";
import {
	CompletedJobDocumentType,
	PractitionerEntityType,
	PatientEntityType,
} from "@/lib/supabase/supabaseTypes";
import { RecipientTypeEnum } from "../types";
import {
	ClientEntityType,
	GuarantorEntityType,
} from "@/lib/supabase/supabaseTypes";
import { LazyPatient } from "@/dentlab/src/store/Patient/patients.store";

interface RecipientGroup {
	recipientType: RecipientTypeEnum;
	recipientId: string;
	clientId: string;
	jobDocuments: CompletedJobDocumentType[];
}

interface SammelrechnungenTableProps {
	recipientGroups: RecipientGroup[];
	dateRange: SelectionRange;
	selectedJobDocumentIds: Record<string, Set<number>>;
	onSelectionChange: (recipientKey: string, selectedIds: Set<number>) => void;
	onOpenPatientDetail: (patientId: string) => void;
	onOpenJobDetail: (jobId: number) => void;
	clientsLookup: Record<string, ClientEntityType>;
	guarantorLookup: Record<string, GuarantorEntityType>;
	practitionersLookup: Record<number, PractitionerEntityType>;
	patientsLookup: Record<string, PatientEntityType | LazyPatient>;
	patientName: (patient: PatientEntityType | LazyPatient) => string;
	onCreateInvoice: (
		recipientKey: string,
		selectedJobDocIds: number[]
	) => void;
	loading?: boolean;
	invoiceDates: Record<string, Date>;
	onInvoiceDateChange: (recipientKey: string, date: Date) => void;
	getSelectedJobsConflicts?: (recipientKey: string) => string[];
	globalInvoiceDate: Date | null;
	akontoLookup?: Record<string, number>;
	akontoEnabled?: Record<string, boolean>;
	onAkontoToggle?: (recipientKey: string) => void;
}

export const SammelrechnungenTable: React.FC<SammelrechnungenTableProps> = ({
	recipientGroups,
	dateRange,
	selectedJobDocumentIds,
	onSelectionChange,
	onOpenPatientDetail,
	onOpenJobDetail,
	clientsLookup,
	guarantorLookup,
	practitionersLookup,
	patientsLookup,
	patientName,
	onCreateInvoice,
	loading = false,
	invoiceDates,
	onInvoiceDateChange,
	getSelectedJobsConflicts,
	globalInvoiceDate,
	akontoLookup,
	akontoEnabled,
	onAkontoToggle,
}) => {
	const [expanded, setExpanded] = useState<ExpandedState>({});
	const [sortState, setSortState] = useState<Record<string, { column: string | null; direction: 'asc' | 'desc' }>>({});

	// Transform data for table
	const baseTableData = useMemo(
		() =>
			transformToTableData(
				recipientGroups,
				selectedJobDocumentIds,
				clientsLookup,
				guarantorLookup,
				patientsLookup,
				practitionersLookup
			),
		[
			recipientGroups,
			selectedJobDocumentIds,
			clientsLookup,
			guarantorLookup,
			patientsLookup,
			practitionersLookup,
		]
	);

	const handleSubrowSort = (recipientKey: string, column: string) => {
		setSortState(prev => {
			const currentSort = prev[recipientKey];
			const currentColumn = currentSort?.column;
			const currentDirection = currentSort?.direction || 'desc';

			if (currentColumn === column) {
				return {
					...prev,
					[recipientKey]: {
						column,
						direction: currentDirection === 'asc' ? 'desc' : 'asc'
					}
				};
			} else {
				return {
					...prev,
					[recipientKey]: {
						column,
						direction: 'desc'
					}
				};
			}
		});
	};

	const tableDataWithSubrowSort = useMemo(() => {
		return baseTableData.map(row => {
			const recipientKey = `${row.clientId}_${row.recipientId}`;
			const sort = sortState[recipientKey];

			if (sort && sort.column) {
				const sortedSubRows = sortJobDocuments(
					row.subRows,
					sort.column,
					sort.direction,
					patientsLookup,
					practitionersLookup,
					clientsLookup
				);
				return {
					...row,
					subRows: sortedSubRows,
					sortColumn: sort.column,
					sortDirection: sort.direction
				};
			}

			return row;
		});
	}, [baseTableData, sortState, patientsLookup, practitionersLookup, clientsLookup]);

	const tableData = useMemo(() => {
		const sorted = [...tableDataWithSubrowSort];

		sorted.sort((a, b) => {
			const aName = a.recipientName.toLowerCase();
			const bName = b.recipientName.toLowerCase();
			return aName.localeCompare(bName);
		});

		return sorted;
	}, [tableDataWithSubrowSort]);

	// Create actions object with useMemo to prevent unnecessary re-renders
	const actions: SammelrechnungTableActions = useMemo(
		() => ({
			onOpenPatientDetail,
			onOpenJobDetail,
			onSelectJobDocument: (
				jobDocumentId: number,
				recipientKey: string
			) => {
				// Parse the composite key
				const [clientId, recipientId] = recipientKey.split('_');

				// Find the recipient using both clientId and recipientId
				const recipient = recipientGroups.find(
					(g) => g.clientId === clientId && g.recipientId === recipientId
				);
				if (!recipient) return;

				const currentSelection =
					selectedJobDocumentIds[recipientKey] || new Set<number>();
				const newSelection = new Set(currentSelection);

				if (newSelection.has(jobDocumentId)) {
					newSelection.delete(jobDocumentId);
				} else {
					newSelection.add(jobDocumentId);
				}

				onSelectionChange(recipientKey, newSelection);
			},
			onCycleRecipientSelection: (recipientKey: string) => {
				// Parse the composite key
				const [clientId, recipientId] = recipientKey.split('_');

				// Find the recipient using both clientId and recipientId
				const recipient = recipientGroups.find(
					(g) => g.clientId === clientId && g.recipientId === recipientId
				);
				if (!recipient) return;

				const currentSelection =
					selectedJobDocumentIds[recipientKey] || new Set<number>();

				// Find the recipient row using both clientId and recipientId
				const recipientRow = tableData.find(
					(row) => row.clientId === clientId && row.recipientId === recipientId
				);
				if (!recipientRow) return;

				const currentState = getCheckboxState(recipientRow, dateRange);
				const availableStates = getAvailableCheckboxStates(
					recipientRow,
					dateRange
				);
				const nextState = getNextCheckboxState(
					currentState,
					availableStates
				);

				let newSelection: Set<number>;

				switch (nextState) {
					case CheckboxState.EMPTY:
						newSelection = new Set<number>();
						break;
					case CheckboxState.DATE_FILTERED: {
						const jobDocsInRange = recipient.jobDocuments.filter(
							(job) => isInDateRange(job, dateRange)
						);
						newSelection = new Set(
							jobDocsInRange.map((job) => job.id as number)
						);
						break;
					}
					case CheckboxState.FULL:
						newSelection = new Set(
							recipient.jobDocuments.map(
								(job) => job.id as number
							)
						);
						break;
					case CheckboxState.PARTIAL:
						// PARTIAL is not a target state, this shouldn't happen
						newSelection = currentSelection;
						break;
					default:
						newSelection = currentSelection;
				}

				onSelectionChange(recipientKey, newSelection);
			},
			onCreateInvoice,
			onInvoiceDateChange,
			getSelectedJobsConflicts,
		}),
		[
			onOpenPatientDetail,
			onOpenJobDetail,
			recipientGroups,
			selectedJobDocumentIds,
			onSelectionChange,
			tableData,
			dateRange,
			onCreateInvoice,
			onInvoiceDateChange,
			getSelectedJobsConflicts,
		]
	);

	// Calculate optimal column widths based on all table data
	const optimalWidths = useMemo(
		() => getOptimalColumnWidths(tableData),
		[tableData]
	);

	// Calculate if there are any conflicts
	const hasConflicts = useMemo(
		() =>
			hasAnyConflictsInData(
				tableData,
				selectedJobDocumentIds,
				getSelectedJobsConflicts
			),
		[tableData, selectedJobDocumentIds, getSelectedJobsConflicts]
	);

	// Create columns with conflicts parameter and tableData
	const columns = useMemo(
		() =>
			createSammelrechnungColumns(
				actions,
				dateRange,
				practitionersLookup,
				clientsLookup,
				patientsLookup,
				patientName,
				optimalWidths,
				invoiceDates,
				globalInvoiceDate,
				hasConflicts,
				tableData,
				akontoLookup,
				akontoEnabled,
				onAkontoToggle,
			),
		[
			dateRange,
			actions,
			practitionersLookup,
			clientsLookup,
			patientsLookup,
			patientName,
			optimalWidths,
			invoiceDates,
			globalInvoiceDate,
			hasConflicts,
			tableData,
			akontoLookup,
			akontoEnabled,
			onAkontoToggle,
		]
	);

	const table = useReactTable({
		data: tableData,
		columns,
		state: {
			expanded,
		},
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSubRows: (row) => {
			if (row.tableRowType === "recipient") {
				return (row as RecipientTableRow).subRows;
			}
			return undefined;
		},
		enableExpanding: true,
	});

	const renderSortableHeader = (
		recipientKey: string,
		label: string,
		columnKey: string,
		className: string,
		sortColumn: string | null,
		sortDirection: 'asc' | 'desc'
	) => {
		const isActive = sortColumn === columnKey;
		const SortIcon = sortDirection === 'asc' ? ArrowUp : ArrowDown;

		return (
			<button
				onClick={(e) => {
					e.stopPropagation();
					handleSubrowSort(recipientKey, columnKey);
				}}
				className={`${className} flex items-center gap-1 hover:text-slate-700 cursor-pointer transition-colors ${
					isActive ? 'text-slate-900 font-semibold' : ''
				}`}
				aria-label={`Sortiere nach ${label}`}
				data-testid={`sort-${columnKey}`}
			>
				<span>{label}</span>
				{isActive && <SortIcon className="w-3 h-3" />}
			</button>
		);
	};

	if (loading) {
		return (
			<div className="flex justify-center py-8">
				<div>Lädt...</div>
			</div>
		);
	}

	if (tableData.length === 0) {
		return (
			<div className="text-center py-8 text-gray-500">
				Keine abgeschlossenen Lieferscheine oder Gutschriften, die noch
				keiner Sammelrechnung angerechnet sind.
			</div>
		);
	}

	return (
		<div className="w-full space-y-4">
			<div className="w-full overflow-x-auto">
				<Table className="w-full min-w-[1000px]">
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead
										key={header.id}
										style={{ width: header.getSize() }}
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef
														.header,
													header.getContext()
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows.map((row, index) => {
							const rowData = row.original;
							const isRecipientRow =
								rowData.tableRowType === "recipient";

							// Check if this is the first job document row after a recipient
							const isFirstJobDocRow =
								!isRecipientRow &&
								(index === 0 ||
									table.getRowModel().rows[index - 1]
										?.original.tableRowType ===
										"recipient");

							const rows = [];

							// Add header row for job documents if this is the first job doc
							if (isFirstJobDocRow) {
								const parentRecipient = row.getParentRow()
									?.original as RecipientTableRow;
								if (parentRecipient) {
									const recipientKey = `${parentRecipient.clientId}_${parentRecipient.recipientId}`;
									const visibleColumns =
										getVisibleColumnsForRecipient(
											parentRecipient
										);

									// Check which conflict columns exist globally (same as in columns definition)
									// But only show them if this specific recipient has conflicts
									const hasGlobalMixedVAT = tableData.some(row => row.hasMixedVAT);
									const hasGlobalMixedMwstNumber = tableData.some(row => row.hasMixedMwstNumber);
									const hasGlobalMixedMwstType = tableData.some(row => row.hasMixedMwstType);
									const hasGlobalMixedRounding = tableData.some(row => row.hasMixedRounding);
									const hasGlobalMixedCurrency = tableData.some(row => row.hasMixedCurrency);

									// Only show headers if this recipient actually has conflicts
									const showVATHeader = hasGlobalMixedVAT && parentRecipient.hasMixedVAT;
									const showMwstNumberHeader = hasGlobalMixedMwstNumber && parentRecipient.hasMixedMwstNumber;
									const showMwstTypeHeader = hasGlobalMixedMwstType && parentRecipient.hasMixedMwstType;
									const showRoundingHeader = hasGlobalMixedRounding && parentRecipient.hasMixedRounding;
									const showCurrencyHeader = hasGlobalMixedCurrency && parentRecipient.hasMixedCurrency;

									rows.push(
										<TableRow
											key={`${row.id}-header`}
											className="bg-slate-50 h-8"
										>
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
											<TableCell className="px-4 py-2 bg-slate-50">
												<div className="pl-4 flex items-center text-sm text-slate-500 font-medium">
													{/* Date */}
													{renderSortableHeader(
														recipientKey,
														'Datum',
														'date',
														optimalWidths.date,
														parentRecipient.sortColumn,
														parentRecipient.sortDirection
													)}

													{/* Patient */}
													{visibleColumns.patient &&
														optimalWidths.patient &&
														renderSortableHeader(
															recipientKey,
															'Patient',
															'patient',
															optimalWidths.patient,
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
													{/* Job */}
													{renderSortableHeader(
														recipientKey,
														'Auftrag',
														'job_code',
														optimalWidths.job,
														parentRecipient.sortColumn,
														parentRecipient.sortDirection
													)}

													{/* Type */}
													{renderSortableHeader(
														recipientKey,
														'Typ',
														'type',
														optimalWidths.type,
														parentRecipient.sortColumn,
														parentRecipient.sortDirection
													)}

													{/* Practitioner (conditional) */}
													{visibleColumns.practitioner &&
														optimalWidths.practitioner &&
														renderSortableHeader(
															recipientKey,
															'Behandler',
															'practitioner',
															optimalWidths.practitioner,
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}

													{/* Client (conditional) */}
													{visibleColumns.client &&
														optimalWidths.client &&
														renderSortableHeader(
															recipientKey,
															'Auftraggeber',
															'client',
															optimalWidths.client,
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}

													{/* Monthly Discount (conditional) */}
													{visibleColumns.monthlyDiscount &&
														optimalWidths.monthlyDiscount &&
														renderSortableHeader(
															recipientKey,
															'Monatsrabatt unterdrückt',
															'monthly_discount',
															optimalWidths.monthlyDiscount,
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</div>
											</TableCell>
											<TableCell className="px-4 py-2 bg-slate-50">
												{renderSortableHeader(
													recipientKey,
													'Betrag',
													'amount',
													'pl-4 text-sm text-slate-500 font-medium text-left',
													parentRecipient.sortColumn,
													parentRecipient.sortDirection
												)}
											</TableCell>
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
											{/* Conflict columns headers - show header only if recipient has conflicts, empty cell if column exists globally */}
											{hasGlobalMixedVAT && (
												<TableCell className="px-4 py-2 bg-slate-50">
													{showVATHeader &&
														renderSortableHeader(
															recipientKey,
															'MwSt.-Satz',
															'tax_rate',
															'pl-4 text-sm text-slate-500 font-medium text-left',
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</TableCell>
											)}
											{hasGlobalMixedMwstNumber && (
												<TableCell className="px-4 py-2 bg-slate-50">
													{showMwstNumberHeader &&
														renderSortableHeader(
															recipientKey,
															'MwSt.-Nr.',
															'mwst_number',
															'pl-4 text-sm text-slate-500 font-medium text-left',
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</TableCell>
											)}
											{hasGlobalMixedMwstType && (
												<TableCell className="px-4 py-2 bg-slate-50">
													{showMwstTypeHeader &&
														renderSortableHeader(
															recipientKey,
															'MwSt.-Typ',
															'mwst_type',
															'pl-4 text-sm text-slate-500 font-medium text-left',
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</TableCell>
											)}
											{hasGlobalMixedRounding && (
												<TableCell className="px-4 py-2 bg-slate-50">
													{showRoundingHeader &&
														renderSortableHeader(
															recipientKey,
															'Rundung',
															'round_to',
															'pl-4 text-sm text-slate-500 font-medium text-left',
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</TableCell>
											)}
											{hasGlobalMixedCurrency && (
												<TableCell className="px-4 py-2 bg-slate-50">
													{showCurrencyHeader &&
														renderSortableHeader(
															recipientKey,
															'Währung',
															'currency',
															'pl-4 text-sm text-slate-500 font-medium text-left',
															parentRecipient.sortColumn,
															parentRecipient.sortDirection
														)}
												</TableCell>
											)}
											{/* Empty cells for Konflikte (if exists), Rechnungsdatum, and Aktionen columns */}
											{hasConflicts && <TableCell className="px-4 py-2 bg-slate-50"></TableCell>}
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
											<TableCell className="px-4 py-2 bg-slate-50"></TableCell>
										</TableRow>
									);
								}
							}

							// Add the actual row
							rows.push(
								<TableRow
									key={row.id}
									className={`
									${
										isRecipientRow
											? "bg-slate-100 font-medium border-b h-12"
											: "bg-white hover:bg-slate-50 h-10"
									}
									${!isRecipientRow ? "border-b-0" : ""}
								`}
									onClick={() => {
										if (
											isRecipientRow &&
											row.getCanExpand()
										) {
											row.toggleExpanded();
										}
									}}
									style={{
										cursor:
											isRecipientRow && row.getCanExpand()
												? "pointer"
												: "default",
									}}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											className="px-4 py-3 text-sm"
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</TableCell>
									))}
								</TableRow>
							);

							return rows;
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};
